import * as Sentry from '@sentry/browser';
import { jwtDecode } from 'jwt-decode';
import { type FloraContextualInfo, FloraVersion } from '../../containers/flora/flora.type';
import { getRefreshToken, getRequestToken, isRefreshTokenValid } from '../../helpers/storage/storage';
import { appConfig } from '../../helpers/app-config/app-config';
import { refreshTokensSingleflight } from '../auth-client/refresh-singleton';
import type { ApprovalMode, AskAgentRequestPayload } from './agent-client.type';

/**
 * How close to its `exp` an access token may be before a socket connect
 * refreshes it first. The backend only validates the token during the
 * handshake, so a small margin covers clock skew and the handshake round trip.
 */
const ACCESS_TOKEN_REFRESH_MARGIN_SECONDS = 30;

/**
 * Longest a socket connect waits on the token refresh before connecting with
 * whatever token is stored. The refresh singleton's own timeout is minutes
 * long; without this cap a hung refresh would leave Flora "thinking" with no
 * error, since callers only recover on the socket's error/close events. The
 * refresh itself keeps running and still persists its rotated tokens.
 */
const REFRESH_BEFORE_CONNECT_TIMEOUT_MS = 10000;

type SocketListenerRegistration = {
  type: string;
  listener: EventListenerOrEventListenerObject;
  options?: boolean | AddEventListenerOptions;
};

/**
 * Checks whether the stored access token is missing its `exp`, malformed, or
 * expires within `ACCESS_TOKEN_REFRESH_MARGIN_SECONDS`.
 *
 * @param {string} token - the encoded access token
 * @returns {boolean} True if the token should be refreshed before connecting
 */
function isAccessTokenStale(token: string): boolean {
  try {
    const { exp } = jwtDecode<{ exp?: number }>(token);
    if (!exp) return true;
    return exp * 1000 - Date.now() < ACCESS_TOKEN_REFRESH_MARGIN_SECONDS * 1000;
  } catch {
    return true;
  }
}

/**
 * WebSocket stand-in returned while the access token is being refreshed.
 *
 * Every Flora socket is opened on demand with the token in its query string,
 * and the axios 401-refresh interceptor never sees WebSocket handshakes — so
 * after the page sits idle past the token lifetime, the next connect would be
 * rejected with 403. This wrapper lets `createAgentWebsocket` stay synchronous
 * (callers assign refs, run stale-socket guards and return cleanups right
 * away) while the real socket waits for the single-flight refresh.
 *
 * It mirrors the WebSocket surface the Flora hooks use: `readyState` reads
 * CONNECTING until the real socket exists, listeners are recorded and attached
 * to the real socket when it is created (so handlers receive genuine events),
 * `send` throws while CONNECTING like the native socket, and `close` before the
 * connect cancels it and fires `error` then `close`, as the browser does when a
 * CONNECTING socket is closed.
 */
class TokenRefreshingWebSocket
  implements Pick<WebSocket, 'readyState' | 'addEventListener' | 'removeEventListener' | 'send' | 'close'>
{
  private socket: WebSocket | null = null;
  private closedBeforeConnect = false;
  private readonly listeners: SocketListenerRegistration[] = [];

  /**
   * @param {() => Promise<unknown>} refresh - refreshes the stored tokens; a rejection still connects
   * @param {() => string} buildUrl - builds the socket URL from the token stored at connect time
   */
  constructor(refresh: () => Promise<unknown>, buildUrl: () => string) {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const deadline = new Promise<void>((resolve) => {
      timeoutId = setTimeout(resolve, REFRESH_BEFORE_CONNECT_TIMEOUT_MS);
    });
    Promise.race([refresh(), deadline])
      // A failed or slow refresh connects with whatever token is stored, so
      // callers get the same error/close events a rejected handshake produced before.
      .catch(() => {
        Sentry.addBreadcrumb({
          category: 'auth',
          level: 'warning',
          message: 'Token refresh before Flora websocket connect failed; connecting with the stored token',
        });
      })
      .then(() => {
        clearTimeout(timeoutId);
        this.connect(buildUrl());
      });
  }

  /**
   * The native socket's readyState once it exists; CONNECTING while the refresh
   * is pending, CLOSED if `close` was called before the connect.
   *
   * @returns {WebSocket["readyState"]} a WebSocket readyState constant
   */
  get readyState(): WebSocket['readyState'] {
    if (this.socket) return this.socket.readyState;
    return this.closedBeforeConnect ? WebSocket.CLOSED : WebSocket.CONNECTING;
  }

  /**
   * Registers a listener, attaching it to the real socket if one exists.
   *
   * @param {string} type - the event type
   * @param {EventListenerOrEventListenerObject} listener - the handler
   * @param {boolean | AddEventListenerOptions} options - native listener options
   * @returns {void}
   */
  addEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions,
  ): void {
    this.listeners.push({ type, listener, options });
    this.socket?.addEventListener(type, listener, options);
  }

  /**
   * Unregisters a listener from the wrapper and the real socket.
   *
   * @param {string} type - the event type
   * @param {EventListenerOrEventListenerObject} listener - the handler
   * @param {boolean | EventListenerOptions} options - native listener options
   * @returns {void}
   */
  removeEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | EventListenerOptions,
  ): void {
    const index = this.listeners.findIndex(
      (registration) => registration.type === type && registration.listener === listener,
    );
    if (index !== -1) this.listeners.splice(index, 1);
    this.socket?.removeEventListener(type, listener, options);
  }

  /**
   * Sends on the real socket; throws while CONNECTING, matching the native API.
   *
   * @param {string} data - the frame to send
   * @returns {void}
   */
  send(data: string): void {
    if (!this.socket) {
      throw new DOMException('WebSocket is still connecting', 'InvalidStateError');
    }
    this.socket.send(data);
  }

  /**
   * Closes the real socket, or cancels the pending connect.
   *
   * @param {number} code - optional close code
   * @param {string} reason - optional close reason
   * @returns {void}
   */
  close(code?: number, reason?: string): void {
    if (this.socket) {
      this.socket.close(code, reason);
      return;
    }
    if (this.closedBeforeConnect) return;
    this.failBeforeConnect();
  }

  /**
   * Marks the wrapper CLOSED and fires `error` then `close` asynchronously, as
   * the browser does when a CONNECTING socket fails.
   *
   * @returns {void}
   */
  private failBeforeConnect(): void {
    this.closedBeforeConnect = true;
    setTimeout(() => {
      this.dispatch(new Event('error'));
      this.dispatch(new CloseEvent('close', { code: 1006, wasClean: false }));
    }, 0);
  }

  /**
   * Opens the real socket and moves the recorded listeners onto it.
   *
   * @param {string} url - the socket URL
   * @returns {void}
   */
  private connect(url: string): void {
    if (this.closedBeforeConnect) return;
    try {
      this.socket = new WebSocket(url);
    } catch (error) {
      // The native constructor throws synchronously to its caller, but that
      // caller is long gone here — surface it as a failed connection instead,
      // so the hooks' error/close handlers clear the pending state.
      Sentry.captureException(error);
      this.failBeforeConnect();
      return;
    }
    for (const { type, listener, options } of this.listeners) {
      this.socket.addEventListener(type, listener, options);
    }
  }

  /**
   * Invokes the recorded listeners for an event raised before the real socket exists.
   *
   * @param {Event} event - the event to deliver
   * @returns {void}
   */
  private dispatch(event: Event): void {
    for (const { type, listener } of [...this.listeners]) {
      if (type !== event.type) continue;
      if (typeof listener === 'function') listener(event);
      else listener.handleEvent(event);
    }
  }
}

/**
 * Creates an agent websocket connection.
 *
 * The access token travels in the query string and is only checked during the
 * handshake. When the stored token is missing, expired or about to expire and a valid
 * refresh token exists, the tokens are refreshed through the shared
 * single-flight before the socket connects; the returned object behaves like a
 * CONNECTING WebSocket in the meantime. Otherwise a native WebSocket is
 * returned immediately, as before.
 *
 * @param {string} organization_id - the id of the organization to connect to
 * @param {FloraVersion} flora_version - the version of flora to use
 * @returns {WebSocket} a websocket connection
 */
function createAgentWebsocket(organization_id: string, flora_version: FloraVersion): WebSocket {
  let versionURL = 'agents';
  if (flora_version === FloraVersion.LANGCHAIN) {
    versionURL = 'flora-langchain';
  }

  let baseURL = appConfig.VITE_APP_API_URL + '/ws/' + versionURL;
  baseURL = baseURL.replace('http', 'ws').replace('https', 'wss');
  const buildUrl = () => baseURL + '/' + organization_id + `/?token=${getRequestToken()}`;

  const accessToken = getRequestToken();
  if ((!accessToken || isAccessTokenStale(accessToken)) && isRefreshTokenValid()) {
    const refresh = () => refreshTokensSingleflight(getRefreshToken() as string);
    return new TokenRefreshingWebSocket(refresh, buildUrl) as unknown as WebSocket;
  }
  return new WebSocket(buildUrl());
}

/**
 * Ask the agent a question via websocket
 * @param {WebSocket | undefined} webSocket - an active websocket connection
 * @param {string} message - the string to send to the agent
 * @param {string} thread_id - an active thread id if one exists
 * @param {string} portfolio_id - optional portfolio id for context
 * @param {FloraContextualInfo} contextualInfo - optional contextual information for the user
 * @param {boolean} enableArtifacts - whether to enable artifact generation and suggested followups
 * @param {string} model - optional Flora model id to override the backend default
 * @param {string | null} effort - optional reasoning effort level to override the model default
 * @param {boolean} isSidebar - whether the request originates from the compact docked side panel, so
 *   the backend can disclose that surface's limitations (chart artifacts do not render there)
 * @param {string[]} documentIds - ids of user-uploaded Word/Excel files attached to this message, so
 *   the backend can persist them on the message and tell Flora to read them
 * @param {ApprovalMode | null} approvalMode - the Ask/Auto mode this turn runs with (the mode the composer
 *   shows); omitted when null so the backend falls back to the chat's saved mode, then the user's default
 * @param {ApprovalMode | null} chatApprovalMode - the chat's own composer choice, which the backend saves on
 *   the chat; omitted when null so an unswitched chat keeps following the user's saved default
 * @returns {void}
 */
function askAgent(
  webSocket: WebSocket | undefined,
  message: string,
  thread_id: string,
  portfolio_id?: string,
  contextualInfo?: FloraContextualInfo,
  enableArtifacts?: boolean,
  model?: string,
  effort?: string | null,
  isSidebar?: boolean,
  documentIds?: string[],
  approvalMode?: ApprovalMode | null,
  chatApprovalMode?: ApprovalMode | null,
): void {
  const payload: AskAgentRequestPayload = {
    message,
    thread_id,
  };

  if (portfolio_id) {
    payload.portfolio_id = portfolio_id;
  }

  if (contextualInfo) {
    payload.contextual_info = contextualInfo;
  }

  if (enableArtifacts) {
    payload.enable_artifacts = enableArtifacts;
  }

  if (model) {
    payload.model = model;
  }

  if (effort) {
    payload.effort = effort;
  }

  if (isSidebar) {
    payload.is_sidebar = isSidebar;
  }

  if (documentIds && documentIds.length > 0) {
    payload.document_ids = documentIds;
  }

  if (approvalMode) {
    payload.approval_mode = approvalMode;
  }

  // Only a chat the user explicitly switched sends this; the backend saves it
  // on the chat, while an unswitched chat keeps following the saved default.
  if (chatApprovalMode) {
    payload.chat_approval_mode = chatApprovalMode;
  }

  webSocket?.send(JSON.stringify(payload));
}

/**
 * Get the chat histories from the agent
 * @param {WebSocket | undefined} webSocket - an active websocket connection
 * @returns {void}
 */
function getChatHistories(webSocket: WebSocket | undefined): void {
  webSocket?.send(
    JSON.stringify({
      type: 'fetch_histories',
    }),
  );
}

/**
 * Delete a chat history from the agent
 * @param {WebSocket | undefined} webSocket - an active websocket connection
 * @param {string} id - the id of the chat history to delete
 * @returns {void}
 */
function deleteChatHistory(webSocket: WebSocket | undefined, id: string): void {
  webSocket?.send(
    JSON.stringify({
      type: 'delete_history',
      thread_id: id,
    }),
  );
}

/**
 * Edit the description of a chat history
 * @param {WebSocket | undefined} webSocket - an active websocket connection
 * @param {string} id - the id of the chat history to edit
 * @param {string} description - the new description of the chat history
 * @returns {void}
 */
function editChatHistoryDescription(webSocket: WebSocket | undefined, id: string, description: string): void {
  webSocket?.send(
    JSON.stringify({
      type: 'edit_history_description',
      thread_id: id,
      description,
    }),
  );
}

/**
 * Reconnect to an existing deep research job and receive accumulated content replay
 * @param {WebSocket | undefined} webSocket - an active websocket connection
 * @param {string} jobId - the ID of the job to reconnect to
 */
function reconnectToJob(webSocket: WebSocket | undefined, jobId: string): void {
  webSocket?.send(
    JSON.stringify({
      type: 'reconnect',
      job_id: jobId,
    }),
  );
}

/**
 * Cancel a running deep research job
 * @param {WebSocket | undefined} webSocket - an active websocket connection
 * @param {string} jobId - the ID of the job to cancel
 */
function cancelJobWs(webSocket: WebSocket | undefined, jobId: string): void {
  webSocket?.send(
    JSON.stringify({
      type: 'cancel_job',
      job_id: jobId,
    }),
  );
}

/**
 * Submit answers to agent questions via websocket
 * @param {WebSocket | undefined} webSocket - an active websocket connection
 * @param {string} jobId - the ID of the agent job that asked the questions
 * @param {Record<string, string | string[]>} answers - map of question text to selected answer(s)
 */
function submitAnswer(
  webSocket: WebSocket | undefined,
  jobId: string,
  answers: Record<string, string | string[]>,
): void {
  webSocket?.send(
    JSON.stringify({
      type: 'submit_answer',
      job_id: jobId,
      answers,
    }),
  );
}

/**
 * Send a tool approval response (approve / approve_always / reject) for HITL gated tools.
 * @param {WebSocket | undefined} webSocket - an active websocket connection
 * @param {string} jobId - the job ID associated with the pending approval
 * @param {Array<{type: 'approve' | 'approve_always'} | {type: 'reject'; message?: string}>} decisions - array of decision objects, e.g. [{type: "approve"}] or [{type: "reject", message: "..."}]
 */
function sendToolApproval(
  webSocket: WebSocket | undefined,
  jobId: string,
  decisions: Array<{ type: 'approve' | 'approve_always' } | { type: 'reject'; message?: string }>,
): void {
  webSocket?.send(
    JSON.stringify({
      type: 'tool_approval_response',
      job_id: jobId,
      decisions,
    }),
  );
}

export {
  askAgent,
  cancelJobWs,
  createAgentWebsocket,
  deleteChatHistory,
  editChatHistoryDescription,
  getChatHistories,
  reconnectToJob,
  sendToolApproval,
  submitAnswer,
};
